import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { 
  Save, 
  RotateCcw, 
  CheckCircle,
  AlertCircle,
  Loader2,
  Layout,
  Type,
  Image as ImageIcon,
  Video,
  Settings,
  ChevronDown,
  ChevronUp,
  History,
  RefreshCw,
  ExternalLink,
  Palette,
  Upload,
  X,
  Trash2,
  Phone,
  Mail,
  Eye,
  EyeOff,
  PanelLeftClose,
  PanelLeft,
  Monitor,
  Smartphone,
  Tablet,
  Star,
  Users
} from 'lucide-react';
import { toast } from 'sonner';
import LandingPreview from '../../components/LandingPreview';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Color presets
const COLOR_PRESETS = [
  { name: 'Azul', value: '#3B82F6' },
  { name: 'Verde', value: '#10B981' },
  { name: 'Vermelho', value: '#EF4444' },
  { name: 'Roxo', value: '#8B5CF6' },
  { name: 'Laranja', value: '#F97316' },
  { name: 'Rosa', value: '#EC4899' },
  { name: 'Amarelo', value: '#EAB308' },
  { name: 'Ciano', value: '#06B6D4' },
];

const BACKGROUND_PRESETS = [
  { name: 'Escuro', value: '#1D262D' },
  { name: 'Escuro Alt', value: '#252F38' },
  { name: 'Preto', value: '#0F172A' },
  { name: 'Cinza', value: '#374151' },
  { name: 'Azul Escuro', value: '#1E3A5F' },
  { name: 'Verde Escuro', value: '#14532D' },
];

// Section header component with collapse functionality
const SectionHeader = ({ title, icon: Icon, expanded, onToggle, children }) => (
  <div className="bg-[#252F38] border border-white/10 rounded-xl overflow-hidden mb-4">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
      data-testid={`section-toggle-${title.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="flex items-center gap-3">
        <Icon className="w-5 h-5 text-blue-400" />
        <span className="text-white font-medium">{title}</span>
      </div>
      {expanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
    </button>
    {expanded && (
      <div className="p-4 pt-0 border-t border-white/10">
        {children}
      </div>
    )}
  </div>
);

// Text input component
const TextInput = ({ label, value, onChange, placeholder, multiline = false, testId }) => (
  <div className="space-y-2">
    <Label className="text-slate-300 text-sm">{label}</Label>
    {multiline ? (
      <Textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="bg-[#1D262D] border-white/10 text-white rounded-lg min-h-[100px]"
        data-testid={testId}
      />
    ) : (
      <Input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="bg-[#1D262D] border-white/10 text-white rounded-lg"
        data-testid={testId}
      />
    )}
  </div>
);

// Color picker component
const ColorPicker = ({ label, value, onChange, presets = COLOR_PRESETS }) => {
  const [showPicker, setShowPicker] = useState(false);
  
  return (
    <div className="space-y-2">
      <Label className="text-slate-300 text-sm">{label}</Label>
      <div className="flex items-center gap-2">
        <button
          onClick={() => setShowPicker(!showPicker)}
          className="w-10 h-10 rounded-lg border-2 border-white/20 hover:border-white/40 transition-colors"
          style={{ backgroundColor: value || '#3B82F6' }}
          data-testid={`color-picker-${label.toLowerCase().replace(/\s/g, '-')}`}
        />
        <Input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#3B82F6"
          className="bg-[#1D262D] border-white/10 text-white rounded-lg flex-1 font-mono text-sm"
        />
      </div>
      {showPicker && (
        <div className="bg-[#1D262D] border border-white/10 rounded-lg p-3 mt-2">
          <div className="grid grid-cols-4 gap-2">
            {presets.map((preset) => (
              <button
                key={preset.value}
                onClick={() => {
                  onChange(preset.value);
                  setShowPicker(false);
                }}
                className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-white/5 transition-colors"
              >
                <div 
                  className="w-8 h-8 rounded-lg border border-white/20" 
                  style={{ backgroundColor: preset.value }}
                />
                <span className="text-xs text-slate-400">{preset.name}</span>
              </button>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-white/10">
            <Label className="text-slate-400 text-xs mb-2 block">Cor personalizada</Label>
            <input
              type="color"
              value={value || '#3B82F6'}
              onChange={(e) => onChange(e.target.value)}
              className="w-full h-10 rounded-lg cursor-pointer"
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Image upload component
const ImageUploader = ({ label, value, onChange, onDelete }) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/admin/content/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      onChange(response.data.url);
      toast.success('Imagem carregada com sucesso!');
    } catch (error) {
      toast.error('Erro ao carregar imagem');
      // Error silenced: error);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!value) return;
    
    const filename = value.split('/').pop();
    try {
      await axios.delete(`${API_URL}/admin/content/delete-image/${filename}`);
      onChange('');
      toast.success('Imagem eliminada');
    } catch (error) {
      // If deletion fails, just clear the value
      onChange('');
    }
  };

  const getFullUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  return (
    <div className="space-y-2">
      <Label className="text-slate-300 text-sm">{label}</Label>
      
      {value ? (
        <div className="relative bg-[#1D262D] border border-white/10 rounded-lg p-3">
          <div className="flex items-start gap-3">
            <img 
              src={getFullUrl(value)} 
              alt="Preview" 
              className="w-20 h-20 object-cover rounded-lg"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <div className="flex-1">
              <p className="text-sm text-slate-400 break-all">{value}</p>
              <div className="flex gap-2 mt-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="border-white/10 text-slate-300 hover:bg-white/5"
                >
                  <Upload className="w-3 h-3 mr-1" />
                  Substituir
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleDelete}
                  className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                >
                  <Trash2 className="w-3 h-3 mr-1" />
                  Remover
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div 
          onClick={() => fileInputRef.current?.click()}
          className="bg-[#1D262D] border-2 border-dashed border-white/20 rounded-lg p-6 text-center cursor-pointer hover:border-blue-500/50 hover:bg-white/5 transition-all"
        >
          {uploading ? (
            <Loader2 className="w-8 h-8 text-blue-400 mx-auto animate-spin" />
          ) : (
            <>
              <ImageIcon className="w-8 h-8 text-slate-500 mx-auto mb-2" />
              <p className="text-slate-400 text-sm">Clique para carregar uma imagem</p>
              <p className="text-slate-500 text-xs mt-1">JPEG, PNG, WebP ou GIF (máx. 5MB)</p>
            </>
          )}
        </div>
      )}
      
      <Input
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Ou cole uma URL de imagem externa..."
        className="bg-[#1D262D] border-white/10 text-white rounded-lg text-sm"
      />
      
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleUpload}
        className="hidden"
      />
    </div>
  );
};

export default function PageBuilder() {
  const [content, setContent] = useState(null);
  const [originalContent, setOriginalContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [version, setVersion] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [showPreview, setShowPreview] = useState(true);
  const [previewDevice, setPreviewDevice] = useState('desktop'); // desktop, tablet, mobile
  const [expandedSections, setExpandedSections] = useState({
    styles: true,
    hero: false,
    howItWorks: false,
    benefits: false,
    testimonials: false,
    videos: false,
    cta: false,
    contact: false
  });

  useEffect(() => {
    fetchContent();
  }, []);

  useEffect(() => {
    if (content && originalContent) {
      setHasChanges(JSON.stringify(content) !== JSON.stringify(originalContent));
    }
  }, [content, originalContent]);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/content/landing`);
      // Ensure styles object exists
      const contentData = response.data.content || {};
      if (!contentData.styles) {
        contentData.styles = {
          primaryColor: '#3B82F6',
          secondaryColor: '#10B981',
          heroBackground: '#1D262D',
          sectionBackground: '#252F38',
          textColor: '#FFFFFF',
          accentColor: '#3B82F6'
        };
      }
      setContent(contentData);
      setOriginalContent(JSON.parse(JSON.stringify(contentData)));
      setVersion(response.data.version || 0);
    } catch (error) {
      // Error silenced: 'Error loading content:', error);
      toast.error('Erro ao carregar conteúdo');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/content/landing/history`);
      setHistory(response.data.history || []);
      setShowHistory(true);
    } catch (error) {
      toast.error('Erro ao carregar histórico');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API_URL}/admin/content/landing`, {
        page_id: 'landing',
        content: content
      });
      setOriginalContent(JSON.parse(JSON.stringify(content)));
      setVersion(response.data.version);
      toast.success('Conteúdo guardado com sucesso!');
    } catch (error) {
      toast.error('Erro ao guardar conteúdo');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Tem certeza que deseja restaurar o conteúdo para o padrão? Esta ação não pode ser desfeita.')) return;
    
    try {
      await axios.post(`${API_URL}/admin/content/landing/reset`);
      await fetchContent();
      toast.success('Conteúdo restaurado para o padrão');
    } catch (error) {
      toast.error('Erro ao restaurar conteúdo');
    }
  };

  const handleRollback = async (targetVersion) => {
    if (!window.confirm(`Restaurar para a versão ${targetVersion}?`)) return;
    
    try {
      await axios.post(`${API_URL}/admin/content/landing/rollback/${targetVersion}`);
      await fetchContent();
      setShowHistory(false);
      toast.success(`Restaurado para versão ${targetVersion}`);
    } catch (error) {
      toast.error('Erro ao restaurar versão');
    }
  };

  const handleDiscard = () => {
    setContent(JSON.parse(JSON.stringify(originalContent)));
    toast.info('Alterações descartadas');
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const updateContent = (path, value) => {
    setContent(prev => {
      const newContent = JSON.parse(JSON.stringify(prev));
      const parts = path.split('.');
      let obj = newContent;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!obj[parts[i]]) obj[parts[i]] = {};
        obj = obj[parts[i]];
      }
      obj[parts[parts.length - 1]] = value;
      return newContent;
    });
  };

  const updateNestedArrayItem = (section, arrayName, index, field, value) => {
    setContent(prev => {
      const newContent = JSON.parse(JSON.stringify(prev));
      if (!newContent[section]) newContent[section] = {};
      if (!newContent[section][arrayName]) newContent[section][arrayName] = [];
      if (!newContent[section][arrayName][index]) newContent[section][arrayName][index] = {};
      newContent[section][arrayName][index][field] = value;
      return newContent;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  // Get preview scale based on device
  const getPreviewScale = () => {
    switch (previewDevice) {
      case 'mobile': return 0.35;
      case 'tablet': return 0.45;
      default: return 0.5;
    }
  };

  // Get preview width based on device
  const getPreviewWidth = () => {
    switch (previewDevice) {
      case 'mobile': return '375px';
      case 'tablet': return '768px';
      default: return '100%';
    }
  };

  return (
    <div className="animate-fade-in" data-testid="page-builder">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Layout className="w-7 h-7 text-blue-400" />
            Editor Visual
          </h1>
          <p className="text-slate-400 mt-1">
            Edite o conteúdo visual do landing page • Versão {version}
          </p>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          {hasChanges && (
            <span className="text-amber-400 text-sm flex items-center gap-1 mr-2" data-testid="unsaved-changes-indicator">
              <AlertCircle className="w-4 h-4" />
              Alterações não guardadas
            </span>
          )}
          
          {/* Preview Toggle */}
          <Button
            variant="outline"
            onClick={() => setShowPreview(!showPreview)}
            className={`border-white/10 text-slate-300 hover:bg-white/5 ${showPreview ? 'bg-blue-500/20 border-blue-500/30' : ''}`}
            data-testid="toggle-preview-btn"
          >
            {showPreview ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
            {showPreview ? 'Ocultar Preview' : 'Ver Preview'}
          </Button>
          
          <Button
            variant="outline"
            onClick={fetchHistory}
            className="border-white/10 text-slate-300 hover:bg-white/5"
            data-testid="history-btn"
          >
            <History className="w-4 h-4 mr-2" />
            Histórico
          </Button>
          
          <Button
            variant="outline"
            onClick={() => window.open('/', '_blank')}
            className="border-white/10 text-slate-300 hover:bg-white/5"
            data-testid="view-site-btn"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Ver Site
          </Button>
          
          <Button
            variant="outline"
            onClick={handleDiscard}
            disabled={!hasChanges}
            className="border-white/10 text-slate-300 hover:bg-white/5"
            data-testid="discard-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Descartar
          </Button>
          
          <Button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="bg-blue-500 hover:bg-blue-600"
            data-testid="save-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Guardar
          </Button>
        </div>
      </div>

      {/* Main Content - Editor + Preview */}
      <div className={`flex gap-6 ${showPreview ? '' : ''}`}>
        {/* Editor Panel */}
        <div className={`${showPreview ? 'w-1/2' : 'w-full'} space-y-4 transition-all duration-300`}>
          {/* Info Banner */}
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-400 mt-0.5" />
              <div>
                <p className="text-blue-400 font-medium">Editor de Conteúdo Visual</p>
                <p className="text-slate-400 text-sm mt-1">
                  {showPreview 
                    ? 'Edite à esquerda e veja as alterações em tempo real à direita.'
                    : 'Edite textos, imagens, cores e vídeos do landing page.'}
                </p>
              </div>
            </div>
          </div>

          {/* Sections */}
          <div className="space-y-4">
        {/* Styles Section */}
        <SectionHeader 
          title="Cores e Estilos" 
          icon={Palette}
          expanded={expandedSections.styles}
          onToggle={() => toggleSection('styles')}
        >
          <div className="space-y-4 mt-4">
            <p className="text-slate-400 text-sm mb-4">
              Configure as cores principais do site. Estas cores serão aplicadas em toda a página.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              <ColorPicker
                label="Cor Principal"
                value={content?.styles?.primaryColor}
                onChange={(v) => updateContent('styles.primaryColor', v)}
              />
              <ColorPicker
                label="Cor Secundária"
                value={content?.styles?.secondaryColor}
                onChange={(v) => updateContent('styles.secondaryColor', v)}
              />
              <ColorPicker
                label="Cor de Destaque"
                value={content?.styles?.accentColor}
                onChange={(v) => updateContent('styles.accentColor', v)}
              />
              <ColorPicker
                label="Fundo Hero"
                value={content?.styles?.heroBackground}
                onChange={(v) => updateContent('styles.heroBackground', v)}
                presets={BACKGROUND_PRESETS}
              />
              <ColorPicker
                label="Fundo Secções"
                value={content?.styles?.sectionBackground}
                onChange={(v) => updateContent('styles.sectionBackground', v)}
                presets={BACKGROUND_PRESETS}
              />
            </div>
          </div>
        </SectionHeader>

        {/* Hero Section */}
        <SectionHeader 
          title="Hero Section" 
          icon={Type}
          expanded={expandedSections.hero}
          onToggle={() => toggleSection('hero')}
        >
          <div className="space-y-4 mt-4">
            <ImageUploader
              label="Imagem de Fundo (opcional)"
              value={content?.hero?.backgroundImage}
              onChange={(v) => updateContent('hero.backgroundImage', v)}
            />
            
            <TextInput
              label="Badge (texto superior)"
              value={content?.hero?.badge}
              onChange={(v) => updateContent('hero.badge', v)}
              placeholder="Ex: Smart Global Financial Insights"
              testId="hero-badge-input"
            />
            <div className="grid md:grid-cols-2 gap-4">
              <TextInput
                label="Título Principal"
                value={content?.hero?.title}
                onChange={(v) => updateContent('hero.title', v)}
                placeholder="Ex: Faça o Seu Dinheiro"
                testId="hero-title-input"
              />
              <TextInput
                label="Título Destacado"
                value={content?.hero?.titleHighlight}
                onChange={(v) => updateContent('hero.titleHighlight', v)}
                placeholder="Ex: Trabalhar Por Si"
                testId="hero-highlight-input"
              />
            </div>
            <TextInput
              label="Subtítulo"
              value={content?.hero?.subtitle}
              onChange={(v) => updateContent('hero.subtitle', v)}
              placeholder="Descrição do serviço..."
              multiline
              testId="hero-subtitle-input"
            />
            <div className="grid md:grid-cols-2 gap-4">
              <TextInput
                label="Botão Principal"
                value={content?.hero?.ctaPrimary}
                onChange={(v) => updateContent('hero.ctaPrimary', v)}
                placeholder="Ex: Começar a Investir"
              />
              <TextInput
                label="Botão Secundário"
                value={content?.hero?.ctaSecondary}
                onChange={(v) => updateContent('hero.ctaSecondary', v)}
                placeholder="Ex: Saber Mais"
              />
            </div>
            
            {/* Stats */}
            <div className="pt-4 border-t border-white/10">
              <Label className="text-slate-300 text-sm mb-3 block">Estatísticas</Label>
              <div className="grid md:grid-cols-3 gap-4">
                {content?.hero?.stats?.map((stat, index) => (
                  <div key={index} className="bg-[#1D262D] rounded-lg p-3 space-y-2">
                    <TextInput
                      label="Valor"
                      value={stat.value}
                      onChange={(v) => updateNestedArrayItem('hero', 'stats', index, 'value', v)}
                      placeholder="Ex: 8.6%"
                    />
                    <TextInput
                      label="Label"
                      value={stat.label}
                      onChange={(v) => updateNestedArrayItem('hero', 'stats', index, 'label', v)}
                      placeholder="Ex: Taxa Média"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </SectionHeader>

        {/* How It Works */}
        <SectionHeader 
          title="Como Funciona" 
          icon={Settings}
          expanded={expandedSections.howItWorks}
          onToggle={() => toggleSection('howItWorks')}
        >
          <div className="space-y-4 mt-4">
            <TextInput
              label="Título da Secção"
              value={content?.howItWorks?.title}
              onChange={(v) => updateContent('howItWorks.title', v)}
            />
            <TextInput
              label="Subtítulo"
              value={content?.howItWorks?.subtitle}
              onChange={(v) => updateContent('howItWorks.subtitle', v)}
            />
            
            <div className="space-y-4 pt-4 border-t border-white/10">
              <Label className="text-slate-300 text-sm">Passos</Label>
              {content?.howItWorks?.steps?.map((step, index) => (
                <div key={index} className="bg-[#1D262D] rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-blue-400 font-bold">Passo {step.step}</span>
                  </div>
                  <TextInput
                    label="Título"
                    value={step.title}
                    onChange={(v) => updateNestedArrayItem('howItWorks', 'steps', index, 'title', v)}
                  />
                  <TextInput
                    label="Descrição"
                    value={step.description}
                    onChange={(v) => updateNestedArrayItem('howItWorks', 'steps', index, 'description', v)}
                    multiline
                  />
                </div>
              ))}
            </div>
          </div>
        </SectionHeader>

        {/* Benefits */}
        <SectionHeader 
          title="Benefícios" 
          icon={CheckCircle}
          expanded={expandedSections.benefits}
          onToggle={() => toggleSection('benefits')}
        >
          <div className="space-y-4 mt-4">
            <TextInput
              label="Título da Secção"
              value={content?.benefits?.title}
              onChange={(v) => updateContent('benefits.title', v)}
            />
            <TextInput
              label="Subtítulo"
              value={content?.benefits?.subtitle}
              onChange={(v) => updateContent('benefits.subtitle', v)}
            />
            
            <div className="space-y-4 pt-4 border-t border-white/10">
              <Label className="text-slate-300 text-sm">Cards de Benefícios</Label>
              <div className="grid md:grid-cols-2 gap-4">
                {content?.benefits?.items?.map((item, index) => (
                  <div key={index} className="bg-[#1D262D] rounded-lg p-4 space-y-3">
                    <TextInput
                      label="Ícone"
                      value={item.icon}
                      onChange={(v) => updateNestedArrayItem('benefits', 'items', index, 'icon', v)}
                      placeholder="TrendingUp, Shield, Wallet, BarChart3"
                    />
                    <TextInput
                      label="Título"
                      value={item.title}
                      onChange={(v) => updateNestedArrayItem('benefits', 'items', index, 'title', v)}
                    />
                    <TextInput
                      label="Descrição"
                      value={item.description}
                      onChange={(v) => updateNestedArrayItem('benefits', 'items', index, 'description', v)}
                      multiline
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4 pt-4 border-t border-white/10">
              <Label className="text-slate-300 text-sm">Lista de Features</Label>
              {content?.benefits?.features?.map((feature, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    value={feature}
                    onChange={(e) => {
                      const newFeatures = [...(content.benefits?.features || [])];
                      newFeatures[index] = e.target.value;
                      updateContent('benefits.features', newFeatures);
                    }}
                    className="bg-[#1D262D] border-white/10 text-white rounded-lg"
                  />
                </div>
              ))}
            </div>
          </div>
        </SectionHeader>

        {/* Testimonials */}
        <SectionHeader 
          title="Testimonios" 
          icon={Star}
          expanded={expandedSections.testimonials}
          onToggle={() => toggleSection('testimonials')}
        >
          <div className="space-y-4 mt-4">
            <TextInput
              label="Título da Secção"
              value={content?.testimonials?.title}
              onChange={(v) => updateContent('testimonials.title', v)}
              placeholder="Lo que dicen nuestros clientes"
            />
            <TextInput
              label="Subtítulo"
              value={content?.testimonials?.subtitle}
              onChange={(v) => updateContent('testimonials.subtitle', v)}
              placeholder="Miles de inversores confían en nosotros"
            />
            
            <div className="space-y-4 pt-4 border-t border-white/10">
              <Label className="text-slate-300 text-sm">Testimonios de Clientes</Label>
              {content?.testimonials?.items?.map((testimonial, index) => (
                <div key={index} className="bg-[#1D262D] rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-purple-400 font-bold">Testimonio {index + 1}</span>
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => updateNestedArrayItem('testimonials', 'items', index, 'rating', star)}
                          className="focus:outline-none"
                        >
                          <Star 
                            className={`w-5 h-5 ${star <= (testimonial.rating || 5) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-600'}`}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                  <TextInput
                    label="Nombre"
                    value={testimonial.name}
                    onChange={(v) => updateNestedArrayItem('testimonials', 'items', index, 'name', v)}
                    placeholder="María González"
                  />
                  <TextInput
                    label="Rol/Profesión"
                    value={testimonial.role}
                    onChange={(v) => updateNestedArrayItem('testimonials', 'items', index, 'role', v)}
                    placeholder="Empresaria"
                  />
                  <TextInput
                    label="Texto del Testimonio"
                    value={testimonial.text}
                    onChange={(v) => updateNestedArrayItem('testimonials', 'items', index, 'text', v)}
                    placeholder="Excelente plataforma..."
                    multiline
                  />
                </div>
              ))}
              
              {/* Add new testimonial button */}
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  const currentItems = content?.testimonials?.items || [];
                  const newItem = { name: '', role: '', rating: 5, text: '', avatar: '' };
                  updateContent('testimonials.items', [...currentItems, newItem]);
                }}
                className="w-full border-dashed border-white/20 text-slate-400 hover:bg-white/5"
              >
                + Agregar Testimonio
              </Button>
            </div>
          </div>
        </SectionHeader>

        {/* Videos */}
        <SectionHeader 
          title="Vídeos" 
          icon={Video}
          expanded={expandedSections.videos}
          onToggle={() => toggleSection('videos')}
        >
          <div className="space-y-4 mt-4">
            <TextInput
              label="Título da Secção"
              value={content?.videos?.title}
              onChange={(v) => updateContent('videos.title', v)}
            />
            <TextInput
              label="Subtítulo"
              value={content?.videos?.subtitle}
              onChange={(v) => updateContent('videos.subtitle', v)}
            />
            
            <div className="space-y-4 pt-4 border-t border-white/10">
              <Label className="text-slate-300 text-sm">Vídeos (YouTube/Vimeo embed URL)</Label>
              {content?.videos?.items?.map((video, index) => (
                <div key={index} className="bg-[#1D262D] rounded-lg p-4 space-y-3">
                  <TextInput
                    label="Título do Vídeo"
                    value={video.title}
                    onChange={(v) => updateNestedArrayItem('videos', 'items', index, 'title', v)}
                  />
                  <TextInput
                    label="Descrição"
                    value={video.description}
                    onChange={(v) => updateNestedArrayItem('videos', 'items', index, 'description', v)}
                  />
                  <TextInput
                    label="URL do Embed (YouTube/Vimeo)"
                    value={video.embedUrl}
                    onChange={(v) => {
                      updateNestedArrayItem('videos', 'items', index, 'embedUrl', v);
                      updateNestedArrayItem('videos', 'items', index, 'placeholder', !v);
                    }}
                    placeholder="https://www.youtube.com/embed/VIDEO_ID"
                  />
                  {video.embedUrl && (
                    <div className="aspect-video bg-black rounded-lg overflow-hidden mt-2">
                      <iframe
                        src={video.embedUrl}
                        title={video.title}
                        className="w-full h-full"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </SectionHeader>

        {/* CTA */}
        <SectionHeader 
          title="Banner CTA" 
          icon={Type}
          expanded={expandedSections.cta}
          onToggle={() => toggleSection('cta')}
        >
          <div className="space-y-4 mt-4">
            <TextInput
              label="Título"
              value={content?.cta?.title}
              onChange={(v) => updateContent('cta.title', v)}
            />
            <TextInput
              label="Subtítulo"
              value={content?.cta?.subtitle}
              onChange={(v) => updateContent('cta.subtitle', v)}
              multiline
            />
            <TextInput
              label="Texto do Botão"
              value={content?.cta?.buttonText}
              onChange={(v) => updateContent('cta.buttonText', v)}
            />
          </div>
        </SectionHeader>

        {/* Contact */}
        <SectionHeader 
          title="Contacto" 
          icon={Mail}
          expanded={expandedSections.contact}
          onToggle={() => toggleSection('contact')}
        >
          <div className="space-y-4 mt-4">
            <TextInput
              label="Título"
              value={content?.contact?.title}
              onChange={(v) => updateContent('contact.title', v)}
            />
            <TextInput
              label="Subtítulo"
              value={content?.contact?.subtitle}
              onChange={(v) => updateContent('contact.subtitle', v)}
            />
            <div className="grid md:grid-cols-3 gap-4">
              <TextInput
                label="Email"
                value={content?.contact?.email}
                onChange={(v) => updateContent('contact.email', v)}
              />
              <TextInput
                label="Telefone"
                value={content?.contact?.phone}
                onChange={(v) => updateContent('contact.phone', v)}
              />
              <TextInput
                label="Localização"
                value={content?.contact?.location}
                onChange={(v) => updateContent('contact.location', v)}
              />
            </div>
            <TextInput
              label="Título Informações Legais"
              value={content?.contact?.legalTitle}
              onChange={(v) => updateContent('contact.legalTitle', v)}
            />
            <TextInput
              label="Texto Legal"
              value={content?.contact?.legalText}
              onChange={(v) => updateContent('contact.legalText', v)}
              multiline
            />
          </div>
        </SectionHeader>
      </div>

          {/* Reset Button */}
          <div className="flex justify-end pt-4 border-t border-white/10">
            <Button
              variant="outline"
              onClick={handleReset}
              className="border-red-500/30 text-red-400 hover:bg-red-500/10"
              data-testid="reset-default-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Restaurar Padrão
            </Button>
          </div>
        </div>

        {/* Preview Panel */}
        {showPreview && (
          <div className="w-1/2 sticky top-4 h-[calc(100vh-120px)]" data-testid="preview-panel">
            {/* Preview Header */}
            <div className="bg-[#252F38] border border-white/10 rounded-t-xl p-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-blue-400" />
                <span className="text-white font-medium text-sm">Preview em Tempo Real</span>
                {hasChanges && (
                  <span className="text-xs text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded">
                    Não guardado
                  </span>
                )}
              </div>
              
              {/* Device Switcher */}
              <div className="flex items-center gap-1 bg-[#1D262D] rounded-lg p-1">
                <button
                  onClick={() => setPreviewDevice('desktop')}
                  className={`p-1.5 rounded transition-colors ${previewDevice === 'desktop' ? 'bg-blue-500/20 text-blue-400' : 'text-slate-400 hover:text-white'}`}
                  title="Desktop"
                >
                  <Monitor className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPreviewDevice('tablet')}
                  className={`p-1.5 rounded transition-colors ${previewDevice === 'tablet' ? 'bg-blue-500/20 text-blue-400' : 'text-slate-400 hover:text-white'}`}
                  title="Tablet"
                >
                  <Tablet className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPreviewDevice('mobile')}
                  className={`p-1.5 rounded transition-colors ${previewDevice === 'mobile' ? 'bg-blue-500/20 text-blue-400' : 'text-slate-400 hover:text-white'}`}
                  title="Mobile"
                >
                  <Smartphone className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            {/* Preview Content */}
            <div 
              className="bg-[#1D262D] border border-t-0 border-white/10 rounded-b-xl overflow-hidden h-[calc(100%-52px)] flex justify-center"
              style={{ backgroundColor: '#0F172A' }}
            >
              <div 
                className="overflow-y-auto overflow-x-hidden transition-all duration-300"
                style={{ 
                  width: getPreviewWidth(),
                  maxWidth: '100%'
                }}
              >
                <LandingPreview content={content} scale={getPreviewScale()} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* History Modal */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="bg-[#252F38] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <History className="w-5 h-5 text-blue-400" />
              Histórico de Versões
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {history.length === 0 ? (
              <p className="text-slate-400 text-center py-8">Nenhum histórico disponível</p>
            ) : (
              history.map((item) => (
                <div 
                  key={item.version}
                  className="bg-[#1D262D] rounded-lg p-4 flex items-center justify-between"
                >
                  <div>
                    <p className="text-white font-medium">Versão {item.version}</p>
                    <p className="text-slate-400 text-sm">
                      {new Date(item.updated_at).toLocaleString('pt-PT')}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleRollback(item.version)}
                    className="border-white/10 text-slate-300 hover:bg-white/5"
                  >
                    <RotateCcw className="w-4 h-4 mr-1" />
                    Restaurar
                  </Button>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
